import { ArrowLeftRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { IStatItem } from "./types";
import { SummaryCard } from "./SummaryCard";
import { formatSummaryNumber, summaryBreakdown } from "./summaryData";
import { PALETTE } from "./constants";
import { rankingSeriesColor } from "./rankingTheme";

export const KpiMultipleCard = ({ item, isTransactions = false }: { item: IStatItem; isTransactions?: boolean }) => {
	const { t, i18n } = useTranslation();
	const { values, total, percentages } = summaryBreakdown(item.data);
	const colors = item.colors?.length ? item.colors : ["#3f9f69", "#d96a77", ...PALETTE];
	const format = (value: number | null) => formatSummaryNumber(value, i18n.language);
	const rows = values.map((value, index) => ({
		value,
		label: item.labels?.[index] || (index < 2 ? t(index === 0 ? "stats.labels.success" : "stats.labels.fail") : t("stats.chart.series", { number: index + 1 })),
		color: rankingSeriesColor(colors[index % colors.length]),
		share: percentages[index],
	}));
	return <SummaryCard title={item.title} icon={<ArrowLeftRight size={20} aria-hidden="true" />} prefix={item.prefix}>
		<div><p className="caption">{t("stats.labels.total")}</p><div className="metric">{format(total)}</div></div>
		<div className="track" role="img" aria-label={rows.map(row => `${row.label}: ${format(row.value)}`).join("; ") || t("common.noData")}>
			{rows.map((row, index) => <span key={index} className="segment" style={{ width: `${row.share ?? 0}%`, background: row.color }} />)}
		</div>
		<dl className="breakdown">
			{rows.map((row, index) => <div key={index}>
				<dt><span className="dot" aria-hidden="true" style={{ background: row.color }} />{row.label}</dt>
				<dd className="count">{format(row.value)}</dd>
				<dd className="share">{row.share === null ? "—" : `${row.share.toLocaleString(i18n.language, { maximumFractionDigits: 1 })}%`}</dd>
			</div>)}
		</dl>
		{values.length === 0 && <p className="caption" role="status">{t("common.noData")}</p>}
		{isTransactions && <p className="caption footnote">{t("stats.summary.failHint")}</p>}
	</SummaryCard>;
};
